import React, { useState, useEffect, useRef } from 'react';
import './VaultPaymentModal.css';

const INVOICE_TARGET_LABELS = {
  me:      { icon: '👤', title: 'Send to Me', desc: 'The invoice will be sent to you directly' },
  hotel:   { icon: '🏨', title: 'Hotel',      desc: 'The hotel will receive and pay the invoice' },
  chain:   { icon: '🔗', title: 'Chain',      desc: 'The chain will receive and pay the invoice' },
  partner: { icon: '🤝', title: 'Partner',    desc: 'Your organisation will receive and pay the invoice' },
};

/**
 * Shared payment modal used by Vault, Sync, and Payment Porter.
 *
 * paymentService shape:
 *   resolve(hotelId, accountId)                              – required
 *   createSetupIntent(accountId, accountName, hotelId)       – required for card; the card is saved on the
 *                                                              hotel's payer, so createSubscription must
 *                                                              get the same hotelId
 *   createSubscription({ accountId, hotelId, productId,
 *     retentionYears, paymentMethodId, stripePriceId })      – optional; if omitted the modal
 *                                                              returns params to the caller
 *   getSubscription(id)                                      – optional (manage mode)
 *   updateSubscriptionCard(id, pmId)                        – optional (manage mode)
 *   resumeSubscription(id)                                   – optional (manage mode)
 *   cancelSubscription(id)                                   – optional (manage mode)
 *   getInvoicePrice(retentionYears, hotelId)                 – optional; if omitted upfrontPrice
 *                                                              from resolve() is used
 *   useFreeSeat({ productId, hotelId, accountId })           – optional; if omitted the result
 *                                                              is returned directly to the caller
 *
 * resolve() must return:
 *   { prices, paymentMethods, billingContacts,
 *     freeSeats, freeSeatLevel, upfrontPrice? }
 *
 * onSuccess is called with one of:
 *   { type: 'subscription', subscriptionId, amount, currency }
 *   { type: 'subscription', paymentMethodId, stripePriceId }  – when createSubscription omitted
 *   { type: 'invoice', invoiceTarget, stripePriceId, contactEmail? }
 *   { type: 'free-seat', level }
 */
const PaymentModal = ({
  hotelId,
  accountId,
  accountName,
  productId,
  invoicePrivilege = 'hotel',
  invoiceTarget: initialInvoiceTarget,
  retentionYears,
  isSuperAdmin,
  currentUser,
  subscriptionId,
  paymentService,
  onSuccess,
  onCancel,
}) => {
  const manageMode = !!subscriptionId;
  const [step, setStep] = useState('loading');
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [invoicePriceData, setInvoicePriceData] = useState(null);
  const [invoicePriceLoading, setInvoicePriceLoading] = useState(false);
  const [invoicePriceError, setInvoicePriceError] = useState(null);
  const [hasInvoiceOption, setHasInvoiceOption] = useState(false);

  const invoiceTargets = invoicePrivilege === 'partner'
    ? ['me', 'hotel', 'chain', 'partner']
    : invoicePrivilege === 'chain'
      ? ['me', 'hotel', 'chain']
      : ['me', 'hotel'];

  const [invoiceTarget, setInvoiceTarget] = useState(initialInvoiceTarget || (invoiceTargets[0] || 'me'));
  const [prices, setPrices] = useState([]);
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [addingCard, setAddingCard] = useState(false);
  const [payError, setPayError] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  const [billingContacts, setBillingContacts] = useState(null);
  const [freeSeats, setFreeSeats] = useState(0);
  const [freeSeatLevel, setFreeSeatLevel] = useState(null);
  const [usingFreeSeat, setUsingFreeSeat] = useState(false);

  const stripeRef = useRef(null);
  const cardElementRef = useRef(null);
  const cardMountRef = useRef(null);

  useEffect(() => {
    if (manageMode) loadSubscription();
    else init();
  }, []); // eslint-disable-line

  const loadSubscription = async () => {
    if (!paymentService.getSubscription) { setPayError('Subscription management not available.'); setStep('error'); return; }
    try {
      const res = await paymentService.getSubscription(subscriptionId);
      setSubscriptionDetails(res.data);
      setStep('manage');
    } catch (err) {
      setPayError(err.response?.data?.error?.message || err.message || 'Failed to load subscription');
      setStep('error');
    }
  };

  const init = async () => {
    try {
      const res = await paymentService.resolve(hotelId, accountId);
      const data = res.data;

      setPrices(data.prices || []);
      setPaymentMethods(data.paymentMethods || []);
      if (data.paymentMethods?.length > 0) setSelectedMethod(data.paymentMethods[0]);

      const contacts = data.billingContacts || {};
      if (currentUser?.email) contacts.me = { email: currentUser.email, contact: currentUser.name || currentUser.email };
      setBillingContacts(contacts);

      setFreeSeats(data.freeSeats || 0);
      setFreeSeatLevel(data.freeSeatLevel || null);

      // Pre-populate invoice price from resolve when no dedicated getInvoicePrice method
      if (data.upfrontPrice && !paymentService.getInvoicePrice) {
        const p = data.upfrontPrice;
        const unitAmt = (p.unit_amount != null ? p.unit_amount / 100 : p.amount) || 0;
        setInvoicePriceData({
          totalAmount: unitAmt,
          unitAmount: unitAmt,
          currency: p.currency,
          lookupKey: p.lookup_key || null,
          priceId: p.id || p.price_id || null,
        });
        setHasInvoiceOption(true);
      } else if (paymentService.getInvoicePrice) {
        setHasInvoiceOption(true);
      }

      setStep('choose-method');
    } catch (err) {
      console.error('Payment resolve failed:', err.message);
      setPayError(err.response?.data?.error?.message || err.message || 'Failed to load payment details');
      setStep('error');
    }
  };

  const handleSelectPlan = (price) => {
    setSelectedPrice(price);
    if (paymentMethods.length > 0) setStep('confirm');
    else setStep('add-card');
  };

  const handleUseFreeSeat = async () => {
    if (paymentService.useFreeSeat) {
      if (!productId) {
        setPayError('Free seats require an existing record. Save it first.');
        return;
      }
      setUsingFreeSeat(true);
      setPayError(null);
      try {
        const res = await paymentService.useFreeSeat({ productId, hotelId, accountId });
        onSuccess({ type: 'free-seat', level: res.data?.level || freeSeatLevel });
      } catch (err) {
        setPayError(err.response?.data?.error?.message || err.message || 'Failed to use free seat');
      } finally {
        setUsingFreeSeat(false);
      }
    } else {
      onSuccess({ type: 'free-seat', level: freeSeatLevel });
    }
  };

  useEffect(() => {
    if (step !== 'invoice') return;
    if (invoicePriceData || invoicePriceLoading) return;
    if (!paymentService.getInvoicePrice) return;
    if (!retentionYears) return;
    setInvoicePriceLoading(true);
    setInvoicePriceError(null);
    paymentService.getInvoicePrice(retentionYears, hotelId)
      .then(res => setInvoicePriceData(res.data))
      .catch(err => setInvoicePriceError(err.response?.data?.error?.message || 'Failed to load invoice price'))
      .finally(() => setInvoicePriceLoading(false));
  }, [step]); // eslint-disable-line

  useEffect(() => {
    if (step !== 'add-card') return;
    mountStripeCard();
  }, [step]); // eslint-disable-line

  const mountStripeCard = async () => {
    const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    if (!publishableKey) { setPayError('Stripe is not configured. Contact support.'); setStep('error'); return; }
    try {
      const { loadStripe } = await import('@stripe/stripe-js');
      const stripe = await loadStripe(publishableKey);
      stripeRef.current = stripe;
      const elements = stripe.elements();
      const card = elements.create('card', {
        style: {
          base: { fontSize: '15px', color: '#374151', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', '::placeholder': { color: '#9ca3af' } },
          invalid: { color: '#dc2626' },
        },
      });
      cardElementRef.current = card;
      setTimeout(() => { if (cardMountRef.current) card.mount(cardMountRef.current); }, 50);
    } catch (err) {
      setPayError('Failed to load payment form: ' + err.message);
      setStep('error');
    }
  };

  const handleSaveCard = async () => {
    if (!stripeRef.current || !cardElementRef.current) return;
    setAddingCard(true);
    setPayError(null);
    try {
      const res = await paymentService.createSetupIntent(accountId, accountName, hotelId);
      const { client_secret } = res.data;
      const result = await stripeRef.current.confirmCardSetup(client_secret, {
        payment_method: { card: cardElementRef.current },
      });
      if (result.error) { setPayError(result.error.message); return; }
      const newPaymentMethodId = result.setupIntent.payment_method;
      if (manageMode) {
        if (paymentService.updateSubscriptionCard) await paymentService.updateSubscriptionCard(subscriptionId, newPaymentMethodId);
        onSuccess({ type: 'card-updated' });
        return;
      }
      const resolveRes = await paymentService.resolve(hotelId, accountId);
      const methods = resolveRes.data.paymentMethods || [];
      setPaymentMethods(methods);
      if (methods.length > 0) { setSelectedMethod(methods[0]); setStep('confirm'); }
    } catch (err) {
      setPayError(err.response?.data?.error?.message || err.message || 'Failed to save card');
    } finally {
      setAddingCard(false);
    }
  };

  const handleSubscribe = async () => {
    if (!selectedMethod || !selectedPrice) return;

    // If the service doesn't create the subscription, return params to the caller
    if (!paymentService.createSubscription) {
      onSuccess({ type: 'subscription', paymentMethodId: selectedMethod.id, stripePriceId: selectedPrice.id });
      return;
    }

    setStep('processing');
    setPayError(null);
    try {
      const res = await paymentService.createSubscription({
        accountId, hotelId, productId: productId || null, retentionYears: retentionYears || null,
        paymentMethodId: selectedMethod.id, stripePriceId: selectedPrice.id,
      });
      const { subscriptionId: newSubId, status, clientSecret, amount, currency } = res.data;

      if (status === 'incomplete' && clientSecret && stripeRef.current) {
        const confirmResult = await stripeRef.current.confirmCardPayment(clientSecret, { payment_method: selectedMethod.id });
        if (confirmResult.error) { setPayError(confirmResult.error.message); setStep('confirm'); return; }
      } else if (status !== 'active') {
        setPayError('Subscription could not be activated. Please check your card details and try again.');
        setStep('confirm');
        return;
      }

      setPaymentResult({ type: 'subscription', subscriptionId: newSubId, amount, currency });
      setStep('success');
    } catch (err) {
      setPayError(err.response?.data?.error?.message || err.message || 'Subscription failed');
      setStep('confirm');
    }
  };

  const handleResumeSubscription = async () => {
    if (!paymentService.resumeSubscription) return;
    setStep('processing');
    setPayError(null);
    try {
      await paymentService.resumeSubscription(subscriptionId);
      onSuccess({ type: 'subscription-resumed' });
    } catch (err) {
      setPayError(err.response?.data?.error?.message || err.message || 'Failed to resume subscription');
      setStep('manage');
    }
  };

  const handleCancelSubscription = async () => {
    if (!paymentService.cancelSubscription) return;
    setStep('processing');
    setPayError(null);
    try {
      const res = await paymentService.cancelSubscription(subscriptionId);
      onSuccess({ type: 'subscription-cancelled', cancelAtPeriodEnd: res.data.cancelAtPeriodEnd, currentPeriodEnd: res.data.currentPeriodEnd });
    } catch (err) {
      setPayError(err.response?.data?.error?.message || err.message || 'Failed to cancel subscription');
      setStep('cancel-confirm');
    }
  };

  const formatPeriodEnd = (ts) => {
    const ms = Number(ts) * 1000;
    if (!ms || isNaN(ms)) return '—';
    return new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const formatPrice = (amount, currency) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

  const cardBrandIcon = () => '💳';

  const planLabel = (price) => {
    const parts = [];
    if (price.nickname) parts.push(price.nickname);
    if (price.recurring) parts.push(`every ${price.recurring}`);
    return parts.join(' · ');
  };

  return (
    <div className="payment-modal-overlay">
      <div className="payment-modal">

        {step === 'loading' && (
          <>
            <h2>Payment Required</h2>
            <p className="step-description">Checking payment details...</p>
          </>
        )}

        {step === 'choose-method' && (
          <>
            <h2>How would you like to pay?</h2>
            <p className="step-description">Choose a payment method.</p>
            <div className="plan-list">
              {freeSeats > 0 && (
                <button
                  className="plan-option"
                  onClick={handleUseFreeSeat}
                  disabled={usingFreeSeat}
                  style={{ borderColor: '#16a34a', background: '#f0fdf4' }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', textAlign: 'left', width: '100%' }}>
                    <span className="plan-price" style={{ fontSize: '1.05rem', color: '#15803d' }}>🎟️ Use Free Seat</span>
                    <span className="plan-nickname">
                      {freeSeatLevel === 'hotel' ? 'Free seat granted to this hotel' : 'Free seat granted to this chain'}
                      {' · '}{freeSeats} available
                    </span>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginTop: '0.4rem', padding: '0.6rem 0.75rem', background: '#dcfce7', borderRadius: '6px', border: '1px solid #86efac' }}>
                      <span style={{ fontSize: '1rem', flexShrink: 0 }}>✅</span>
                      <span style={{ fontSize: '0.85rem', color: '#15803d', fontWeight: 600, lineHeight: 1.4 }}>
                        {usingFreeSeat ? 'Consuming seat…' : 'No payment required — access granted immediately.'}
                      </span>
                    </div>
                  </div>
                </button>
              )}
              {prices.length > 0 && (
                <button className="plan-option plan-option-card" onClick={() => setStep('choose-plan')}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', textAlign: 'left', width: '100%' }}>
                    <span className="plan-price" style={{ fontSize: '1.05rem' }}>💳 Pay by Card</span>
                    <span className="plan-nickname">Pay now using a debit or credit card</span>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginTop: '0.4rem', padding: '0.6rem 0.75rem', background: '#dcfce7', borderRadius: '6px', border: '1px solid #86efac' }}>
                      <span style={{ fontSize: '1rem', flexShrink: 0 }}>✅</span>
                      <span style={{ fontSize: '0.85rem', color: '#15803d', fontWeight: 600, lineHeight: 1.4 }}>
                        Access is granted immediately once payment is confirmed.
                      </span>
                    </div>
                  </div>
                </button>
              )}
              {hasInvoiceOption && (
                <button className="plan-option plan-option-invoice" onClick={() => setStep('invoice')}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', textAlign: 'left', width: '100%' }}>
                    <span className="plan-price" style={{ fontSize: '1.05rem' }}>🧾 Invoice</span>
                    <span className="plan-nickname">Request an invoice — pay by bank transfer</span>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginTop: '0.4rem', padding: '0.6rem 0.75rem', background: '#fef3c7', borderRadius: '6px', border: '1px solid #fcd34d' }}>
                      <span style={{ fontSize: '1rem', flexShrink: 0 }}>⚠️</span>
                      <span style={{ fontSize: '0.85rem', color: '#92400e', fontWeight: 600, lineHeight: 1.4 }}>
                        Processing is blocked until the invoice is paid — this will cause a delay.
                      </span>
                    </div>
                  </div>
                </button>
              )}
            </div>
            {payError && <div className="payment-error" style={{ marginTop: '0.75rem' }}>{payError}</div>}
            <div className="payment-modal-actions">
              <button className="btn-cancel-payment" onClick={onCancel}>Cancel</button>
            </div>
          </>
        )}

        {step === 'choose-plan' && (
          <>
            <h2>Choose a Plan</h2>
            <p className="step-description">Select a billing option.</p>
            <div className="plan-list">
              {prices.filter(p => p.recurring === 'month' || p.recurring === 'year').map(price => (
                <button key={price.id} className="plan-option" onClick={() => handleSelectPlan(price)}>
                  <span className="plan-price">{formatPrice(price.amount, price.currency)}</span>
                  {price.recurring && <span className="plan-interval">/ {price.recurring}</span>}
                  {price.nickname && <span className="plan-nickname">{price.nickname}</span>}
                  {isSuperAdmin && price.lookup_key && (
                    <span style={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace', marginTop: '4px' }}>{price.lookup_key}</span>
                  )}
                </button>
              ))}
            </div>
            <div className="payment-modal-actions">
              <button className="btn-cancel-payment" onClick={() => setStep('choose-method')}>Back</button>
            </div>
          </>
        )}

        {step === 'invoice' && (
          <>
            <h2>Request Invoice</h2>
            {invoicePriceLoading && <p className="step-description">Loading invoice price...</p>}
            {invoicePriceError && <div className="payment-error">{invoicePriceError}</div>}
            {invoicePriceData && (
              <div style={{ margin: '1rem 0', padding: '1rem 1.25rem', background: '#f5f3ff', border: '2px solid #6366f1', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                  Invoice total{retentionYears ? ` (${retentionYears} year${retentionYears !== 1 ? 's' : ''} retention)` : ''}
                </div>
                <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1e1b4b' }}>
                  {formatPrice(invoicePriceData.totalAmount, invoicePriceData.currency)}
                </div>
                {retentionYears && invoicePriceData.unitAmount !== invoicePriceData.totalAmount && (
                  <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    {formatPrice(invoicePriceData.unitAmount, invoicePriceData.currency)} × {retentionYears} year{retentionYears !== 1 ? 's' : ''}
                  </div>
                )}
                {isSuperAdmin && invoicePriceData.lookupKey && (
                  <div style={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace', marginTop: '6px' }}>{invoicePriceData.lookupKey}</div>
                )}
              </div>
            )}
            {invoiceTargets.length > 0 && (
              <>
                <p className="step-description" style={{ marginTop: '1.25rem' }}>Invoice addressed to:</p>
                <div className="plan-list">
                  {invoiceTargets.map(target => {
                    const lbl = INVOICE_TARGET_LABELS[target];
                    const billing = billingContacts?.[target];
                    const hasEmail = !!(billing?.email);
                    const isDisabled = !hasEmail;
                    return (
                      <button
                        key={target}
                        className={`plan-option${invoiceTarget === target && !isDisabled ? ' plan-option-selected' : ''}${isDisabled ? ' plan-option-disabled' : ''}`}
                        onClick={() => !isDisabled && setInvoiceTarget(target)}
                        disabled={isDisabled}
                        style={{ textAlign: 'left' }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                          <span className="plan-price" style={{ fontSize: '1rem' }}>{lbl.icon} {lbl.title}</span>
                          <span className="plan-nickname">{lbl.desc}</span>
                          {hasEmail ? (
                            <div style={{ marginTop: '0.4rem', fontSize: '0.82rem', color: '#374151', lineHeight: 1.5 }}>
                              {billing.contact && <div><strong>{billing.contact}</strong></div>}
                              <div>{billing.email}</div>
                              {billing.phone && <div>{billing.phone}</div>}
                            </div>
                          ) : (
                            <div style={{ marginTop: '0.4rem', fontSize: '0.82rem', color: '#9ca3af', fontStyle: 'italic' }}>
                              No billing email on record — option unavailable
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
            <div className="payment-modal-actions">
              <button className="btn-cancel-payment" onClick={() => setStep('choose-method')}>Back</button>
              <button
                className="btn-pay"
                disabled={invoicePriceLoading || (retentionYears && !invoicePriceData) || !billingContacts?.[invoiceTarget]?.email}
                onClick={() => onSuccess({
                  type: 'invoice',
                  invoiceTarget,
                  stripePriceId: invoicePriceData?.priceId || null,
                  contactEmail: invoiceTarget === 'me' ? billingContacts?.me?.email : undefined,
                })}
              >
                Confirm Invoice
              </button>
            </div>
          </>
        )}

        {step === 'add-card' && (
          <>
            <h2>Add Payment Method</h2>
            {selectedPrice && (
              <>
                <div className="price-summary">{formatPrice(selectedPrice.amount, selectedPrice.currency)}</div>
                <div className="price-label">{planLabel(selectedPrice) || 'per period'}</div>
              </>
            )}
            <p className="step-description">
              Enter your card details. Your card will be saved for future purchases.
            </p>
            <div className="card-element-wrapper">
              <div ref={cardMountRef} />
            </div>
            {payError && <div className="payment-error">{payError}</div>}
            <div className="payment-modal-actions">
              <button className="btn-cancel-payment" onClick={() => setStep(manageMode ? 'manage' : 'choose-plan')}>Back</button>
              <button className="btn-pay" onClick={handleSaveCard} disabled={addingCard}>
                {addingCard ? <><span className="spinner" />Saving...</> : (manageMode ? 'Update Card' : 'Save Card & Continue')}
              </button>
            </div>
          </>
        )}

        {step === 'confirm' && selectedMethod && selectedPrice && (
          <>
            <h2>Start Subscription</h2>
            <div className="price-summary">{formatPrice(selectedPrice.amount, selectedPrice.currency)}</div>
            <div className="price-label">{planLabel(selectedPrice) || 'per period'}</div>
            <div className="saved-card">
              <span className="card-icon">{cardBrandIcon()}</span>
              <div>
                <div className="card-brand">{selectedMethod.brand}</div>
                <div className="card-detail">
                  •••• {selectedMethod.last4} &nbsp;·&nbsp; {selectedMethod.exp_month}/{selectedMethod.exp_year}
                </div>
              </div>
            </div>
            <button className="use-different-card" onClick={() => setStep('add-card')}>
              Use a different card
            </button>
            {payError && <div className="payment-error">{payError}</div>}
            <div className="payment-modal-actions">
              <button className="btn-cancel-payment" onClick={() => setStep('choose-plan')}>Back</button>
              <button className="btn-pay" onClick={handleSubscribe}>
                Subscribe — {formatPrice(selectedPrice.amount, selectedPrice.currency)}{selectedPrice.recurring ? ` / ${selectedPrice.recurring}` : ''}
              </button>
            </div>
          </>
        )}

        {step === 'processing' && (
          <>
            <h2>Processing Payment</h2>
            <p className="step-description">Please wait while we process your payment...</p>
          </>
        )}

        {step === 'success' && (
          <>
            <div className="payment-success-icon">✅</div>
            <h2>Subscription Active</h2>
            {paymentResult && (
              <p className="step-description">
                Your subscription for {formatPrice(paymentResult.amount, paymentResult.currency)} is now active.
              </p>
            )}
            <div className="payment-modal-actions">
              <button className="btn-pay" onClick={() => onSuccess(paymentResult)}>Continue</button>
            </div>
          </>
        )}

        {step === 'manage' && subscriptionDetails && (
          <>
            <h2>Subscription</h2>
            <div style={{ margin: '1rem 0', padding: '1rem 1.25rem', background: subscriptionDetails.cancelAtPeriodEnd ? '#fef3c7' : '#f0fdf4', border: `1px solid ${subscriptionDetails.cancelAtPeriodEnd ? '#fcd34d' : '#86efac'}`, borderRadius: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <span style={{ fontWeight: 700, fontSize: '1.1rem', color: '#111827' }}>
                  {subscriptionDetails.amount != null ? formatPrice(subscriptionDetails.amount, subscriptionDetails.currency) : '—'}
                  {subscriptionDetails.interval && <span style={{ fontWeight: 400, fontSize: '0.9rem', color: '#6b7280' }}> / {subscriptionDetails.intervalCount > 1 ? `${subscriptionDetails.intervalCount} ` : ''}{subscriptionDetails.interval}</span>}
                </span>
                <span style={{ fontSize: '12px', fontWeight: 600, padding: '3px 8px', borderRadius: '12px', background: subscriptionDetails.status === 'active' ? '#dcfce7' : '#fee2e2', color: subscriptionDetails.status === 'active' ? '#166534' : '#991b1b' }}>
                  {subscriptionDetails.cancelAtPeriodEnd ? 'Cancels' : subscriptionDetails.status}
                </span>
              </div>
              {subscriptionDetails.cancelAtPeriodEnd ? (
                <div style={{ fontSize: '13px', color: '#92400e' }}>Active until {subscriptionDetails.renewalDate || formatPeriodEnd(subscriptionDetails.currentPeriodEnd)} — will not renew</div>
              ) : (
                <div style={{ fontSize: '13px', color: '#374151' }}>Renews {subscriptionDetails.renewalDate || formatPeriodEnd(subscriptionDetails.currentPeriodEnd)}</div>
              )}
              {subscriptionDetails.card && (
                <div style={{ marginTop: '0.75rem', fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  💳 {subscriptionDetails.card.brand} •••• {subscriptionDetails.card.last4} &nbsp;·&nbsp; {subscriptionDetails.card.exp_month}/{subscriptionDetails.card.exp_year}
                </div>
              )}
              {isSuperAdmin && subscriptionDetails.lookupKey && (
                <div style={{ marginTop: '6px', fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace' }}>{subscriptionDetails.lookupKey}</div>
              )}
              {isSuperAdmin && subscriptionDetails.id && (
                <div style={{ marginTop: '4px', fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace' }}>{subscriptionDetails.id}</div>
              )}
            </div>
            <div className="payment-modal-actions" style={{ flexDirection: 'column', gap: '8px' }}>
              <button className="btn-pay" style={{ width: '100%' }} onClick={() => setStep('add-card')}>Update Card</button>
              {subscriptionDetails.cancelAtPeriodEnd ? (
                <button className="btn-pay" style={{ width: '100%', background: '#16a34a' }} onClick={handleResumeSubscription}>Resume Subscription</button>
              ) : (
                <button className="btn-cancel-payment" style={{ width: '100%', color: '#dc2626', borderColor: '#dc2626' }} onClick={() => setStep('cancel-confirm')}>Cancel Subscription</button>
              )}
              <button className="btn-cancel-payment" style={{ width: '100%' }} onClick={onCancel}>Close</button>
            </div>
          </>
        )}

        {step === 'cancel-confirm' && subscriptionDetails && (
          <>
            <h2>Cancel Subscription?</h2>
            <p className="step-description">
              Your subscription will remain active until <strong>{subscriptionDetails.renewalDate || formatPeriodEnd(subscriptionDetails.currentPeriodEnd)}</strong>, after which it will not renew and access may be affected.
            </p>
            {payError && <div className="payment-error">{payError}</div>}
            <div className="payment-modal-actions">
              <button className="btn-cancel-payment" onClick={() => setStep('manage')}>Back</button>
              <button className="btn-pay" style={{ background: '#dc2626' }} onClick={handleCancelSubscription}>Confirm Cancellation</button>
            </div>
          </>
        )}

        {step === 'error' && (
          <>
            <h2>Payment Error</h2>
            <div className="payment-error">{payError || 'An unexpected error occurred.'}</div>
            <div className="payment-modal-actions">
              <button className="btn-cancel-payment" onClick={onCancel}>Cancel</button>
              <button className="btn-pay" onClick={() => { setStep('loading'); init(); }}>Retry</button>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default PaymentModal;
